import {
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListItem, MatNavList } from '@angular/material/list';
import {
  MatDrawerMode,
  MatSidenavContainer,
  MatSidenavContent,
  MatSidenavModule,
} from '@angular/material/sidenav';
import { RouterModule, RouterOutlet } from '@angular/router';
import { checkExpiration } from '@app/core/utils/expire-checker.utils';
import { SnackbarMessageService } from '@core/services/snackbar-message.service';
import { TokenMonitorService } from '@core/services/token-monitor.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { AuthService } from 'app/auth/auth.service';
import { LogoutComponent } from './auth/logout/logout.component';
import { HeaderComponent } from './components/header/header.component';
import { LoadingOverlayComponent } from './components/loading-overlay/loading-overlay.component';
import { LoadingService } from './components/loading-overlay/services/loading.service';
import { SIDENAV } from './core/constants/app.constants';
import { UserRole } from './core/constants/roles.enum';
import { NavigationRoutesService } from './core/services/navigation-routes.service';
import { SocketService } from './core/services/socket.service';
import { NavtabSocketService } from './services/navtab-socket.service';
import { filter, tap } from 'rxjs';
import { UserService } from './services/user.service';
import { ManagedDoctorsService } from './services/managed-doctors.service';

@Component({
  selector: 'app-root',
  imports: [
    HeaderComponent,
    RouterOutlet,
    RouterModule,
    LoadingOverlayComponent,
    MatIcon,
    MatListItem,
    MatNavList,
    MatSidenavModule,
    MatSidenavContent,
    MatSidenavContainer,
    TranslocoPipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private readonly navigationRoutes = inject(NavigationRoutesService);

  // NOTE This service monitors if the token is expired and does logout in private routes.
  private readonly tokenMonitor = inject(TokenMonitorService);
  private readonly snackbarMessageService = inject(SnackbarMessageService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly socketService = inject(SocketService);
  private readonly navtabSocket = inject(NavtabSocketService);
  private readonly managedDoctorsService = inject(ManagedDoctorsService);

  loadingService = inject(LoadingService);

  isMobile = window.innerWidth < 960;
  showNavList = !this.isMobile;
  sidenavMode: MatDrawerMode = 'side';

  readonly isAuthenticated = this.authService.isAuthenticated;

  // Signal que guarda la info decodificada, dependiendo del valor del token
  readonly tokenData = computed(() => {
    this.authService.token();
    return this.authService.getDecodedToken();
  });

  readonly hideMenu = computed(() =>
    this.navigationRoutes.getHideMenuAndHeader()
  );

  readonly sidenavMenuItems = computed(() => {
    const role = this.tokenData().role;

    // Visitors are the only paying users, so they are the only ones an expired trial can
    // lock out — and then the whole menu goes, leaving the profile side to offer paying or
    // leaving. PrivateWithRoleGuard applies the same rule, so the two cannot disagree.
    if (role === UserRole.VISITOR && this.checkExpirationAndSubscription()) {
      return [];
    }

    let items = SIDENAV.filter((item) => item.type !== 'profile');
    if (role !== UserRole.ADMIN) {
      items = items.filter((item) => item.route !== '/admin');
    }
    // QualiPass: the reception day view is account-scoped, so it only means anything to a
    // doctor or their receptionist, and only once the account has QualiPass switched on.
    // ClinicStaffGuard still lets an admin open it by URL for support, but an admin's own
    // account has no doctors and the page would just be empty.
    if (
      !(
        (role === UserRole.DOCTOR || role === UserRole.RECEPTIONIST) &&
        this.tokenData().qualipassEnabled === true
      )
    ) {
      items = items.filter((item) => item.route !== '/clinic-today');
    }
    if (role !== UserRole.VISITOR) {
      items = items.filter((item) => item.route !== '/my-visits');
    }
    return items;
  });

  readonly sidenavProfileItems = computed(() => {
    const role = this.tokenData().role;
    let items = SIDENAV.filter((item) => item.type !== 'menu');

    // Read-only receptionists: only Settings (change-password) and Logout — no profile/subscription.
    if (role === UserRole.RECEPTIONIST) {
      return items.filter(
        (item) => item.route === '/settings' || item.route === '/logout'
      );
    }

    // The visitor report is a doctor's view of the reps who visit them.
    if (role !== UserRole.DOCTOR) {
      items = items.filter((item) => item.route !== '/visitor-report');
    }

    // Subscriptions belong to visitors. SubscriptionGuard sends every other role to the
    // calendar, so showing the entry to anyone else offers a link that cannot work — which
    // is exactly what an admin saw.
    if (role !== UserRole.VISITOR) {
      items = items.filter((item) => item.route !== '/subscription');
    }

    // Trial over and nothing bought: leave the visitor the way to pay and the way out.
    if (role === UserRole.VISITOR && this.checkExpirationAndSubscription()) {
      items = items.filter(
        (item) => item.route === '/subscription' || item.route === '/logout'
      );
    }

    return items;
  });

  notificationsCount = this.navtabSocket.getUnreadNotificationsCount();
  chatsCount = this.navtabSocket.getUnreadChatsCount();

  constructor() {
    this.initSocketConnection();
    this.changeNotificationsNumber();
    this.initManagedDoctors();
  }

  ngAfterViewInit() {
    // Diferimos sidenavMode al siguiente microtask para evitar NG0100 cuando [mode] depende de isMobile tras el primer check.
    queueMicrotask(() => {
      this.sidenavMode = this.isMobile ? 'over' : 'side';
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent) {
    const target = event.target as Window;
    this.isMobile = target.innerWidth < 959.98;
    this.showNavList = !this.isMobile;
    this.sidenavMode = this.isMobile ? 'over' : 'side';
  }

  toggleSidenav() {
    this.showNavList = !this.showNavList;
  }

  private initSocketConnection() {
    effect(() => {
      const token = this.tokenData();
      // Couriers only tap the badge; they have no chat or notifications to stream.
      if (token && token.role !== UserRole.REPARTO) {
        this.socketService.initSocketConnection();
      }
    });
  }

  private changeNotificationsNumber() {
    effect(() => {
      const notiCount = this.navtabSocket.getUnreadNotificationsCount();
      this.notificationsCount = notiCount;

      const chatCount = this.navtabSocket.getUnreadChatsCount();
      this.chatsCount = chatCount;
    });
  }

  private initManagedDoctors() {
    effect(() => {
      const token = this.tokenData();
      if (token.role === UserRole.DOCTOR || token.role === UserRole.RECEPTIONIST) {
        this.managedDoctorsService.loadManagedDoctors()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      }
    });
  }

  private checkExpirationAndSubscription(): boolean {
    return (
      checkExpiration(this.tokenData().trialPeriodExpired!).hasExpired &&
      !this.tokenData().subscriptionId
    );
  }

  logout() {
    this.dialog
      .open(LogoutComponent, {
        width: '312px',
        maxWidth: '560px',
        height: '200px',
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .pipe(
        filter((confirm) => !!confirm),
        tap(() => {
          this.socketService.disconnect();
          this.socketService.stopManualPing();
          this.authService.logout();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
